import Button from "react-bootstrap/Button";
import React, { useState, useEffect } from "react";
import Form from "react-bootstrap/Form";
import Modal from "react-bootstrap/Modal";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Alert from "react-bootstrap/Alert";
import {
  createTemplate,
  getPractice,
  getUsersByPracticeNumber,
  uploadImage,
} from "../../api";
import { FaCheck, FaTimes } from "react-icons/fa";
import { jwtDecode } from "jwt-decode";
import "./AddTemplate.css";


function AddTemplateModal(props) {
  const [templateTitle, setTemplateTitle] = useState("");
  const [templateDescription, setTemplateDescription] = useState("");
  const [templateStatus, setTemplateStatus] = useState("Active");
  const [templatePracticeId, setTemplatePracticeId] = useState("");
  const [templateInitial, setTemplateInitial] = useState("initial");
  const [notes, setNotes] = useState([]); // Stores added sections
  const [successAlert, setSuccessAlert] = useState(false);
  const [closeAlert, setCloseAlert] = useState(false);
  const [errorAlert, setErrorAlert] = useState(null);
  const [pracName, setPracName] = useState([]);
  const [uniqueTeamMember, setUniqueTeamMember] = useState([]);
  const [selectedPractices, setSelectedPractices] = useState([]);
  const [errors, setErrors] = useState({});
  const [templateData, setTemplateData] = useState({
    templateTitle: "",
    templateDescription: "",
    templateStatus: "Active",
    templatePracticeId: "",
    templateInitial: "initial",
    notes: [],
  });

  useEffect(() => {
    async function loadData() {
      const token = sessionStorage.getItem("user");
      if (token) {
        const decoded = jwtDecode(token);
        const practices = await getPractice();
        const userPractices = practices.filter((prac) =>
          decoded.practice.includes(prac.practiceNumber)
        );
        setPracName(userPractices);

        const teamMemberPromises = userPractices.map((practice) =>
          getUsersByPracticeNumber(practice.practiceNumber)
        );
        const teamMembers = (await Promise.all(teamMemberPromises)).flat();

        // Deduplicate team members by _id
        const uniqueTeamMembers = Array.from(
          new Map(teamMembers.map((member) => [member._id, member])).values()
        );
        setUniqueTeamMember(uniqueTeamMembers);
      }
    }
    loadData();
  }, []);

  const validateForm = () => {
    const newErrors = {};

    // ✅ General Template Validations
    if (!templateData.templateTitle)
      newErrors.templateTitle = "Template Title is required.";
    if (!templateData.templateDescription)
      newErrors.templateDescription = "Template Description is required.";
    if (!templateData.templateStatus)
      newErrors.templateStatus = "Template Status is required.";
    if (!templateData.templatePracticeId.length)
      newErrors.templatePracticeId = "At least one practice is required.";
    if (!templateData.templateInitial)
      newErrors.templateInitial = "Template Type is required.";

    // ✅ Notes Validations (Loop Through Sections)
    notes.forEach((note, index) => {
      if (!note.sessionSection) {
        newErrors[`notes-${index}-sessionSection`] =
          "Section Name is required.";
      }

      if (
        note.sessionFieldType === "multiselect" &&
        (!note.sessionSectionNotes || note.sessionSectionNotes.length === 0)
      ) {
        newErrors[`notes-${index}-sessionSectionNotes`] =
          "At least one option is required.";
      }

      if (note.sessionFieldType === "image") {
        const file = note.sessionSectionNotes;

        if (!file) {
          newErrors[`notes-${index}-sessionSectionNotes`] =
            "Image upload is required.";
        } else {
          // ✅ Validate File Type
          const allowedTypes = ["image/png", "image/jpeg", "image/jpg"];
          if (!allowedTypes.includes(file.type)) {
            newErrors[`notes-${index}-sessionSectionNotes`] =
              "Only PNG, JPEG, and JPG files are allowed.";
          }

          // ✅ Validate File Size (Max 2MB)
          const maxSize = 2 * 1024 * 1024; // 2MB
          if (file.size > maxSize) {
            newErrors[`notes-${index}-sessionSectionNotes`] =
              "File size must be less than 2MB.";
          }
        }
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handlePracticeChange = (practiceNumber) => {
    setSelectedPractices((prev) => {
      const updated = prev.includes(practiceNumber)
        ? prev.filter((p) => p !== practiceNumber)
        : [...prev, practiceNumber];
  
      setTemplateData((prev) => ({
        ...prev,
        templatePracticeId: updated, // ✅ Ensure it's stored correctly
      }));
  
      return updated;
    });
  };

  // ✅ Add a new section dynamically
  const handleAddSection = () => {
    setNotes([
      ...notes,
      { sessionSection: "", sessionFieldType: "text", sessionSectionNotes: "" },
    ]);
  };

  // ✅ Update a specific section's data
  const handleSectionChange = (index, field, value) => {
    const updatedNotes = [...notes];
    updatedNotes[index][field] = value;
    setNotes(updatedNotes);
  };

  // ✅ Remove a section
  const handleRemoveSection = (index) => {
    setNotes(notes.filter((_, i) => i !== index));
  };

  // ✅ Handle multi-select option additions
  const handleMultiSelectOptions = (index, value) => {
    const updatedNotes = [...notes];
    updatedNotes[index].sessionSectionNotes = value
      .split(",")
      .map((v) => v.trim()); // Store as array
    setNotes(updatedNotes);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    let updatedNotes = [...notes];

    // ✅ Check if any section has an image that needs uploading
    for (let i = 0; i < updatedNotes.length; i++) {
      if (
        updatedNotes[i].sessionFieldType === "image" &&
        updatedNotes[i].sessionSectionNotes
      ) {
        const imageFile = updatedNotes[i].sessionSectionNotes; // Get uploaded image file
        const formData = new FormData();
        formData.append("image", imageFile);
        formData.append("x-image-caption", "Template Image Upload");

        try {
          console.log("📤 Uploading image...");
          const response = await uploadImage(formData);
          updatedNotes[i].sessionImageURL = response.imageUrl; // ✅ Store uploaded image URL in `sessionsImageURL`
          console.log("✅ Image uploaded:", response.imageUrl);
        } catch (error) {
          console.error("⛔ Upload failed:", error.message);
          setErrorAlert("Image upload failed. Please try again.");
          return; // ❌ Stop form submission if image upload fails
        }
      }
    }

    // ✅ Construct template data
    const templateData = {
      templateTitle,
      templateDescription,
      templateStatus,
      templatePracticeId: selectedPractices,
      templateInitial,
      notes: updatedNotes, // ✅ Save updated notes with images
    };

    try {
      await createTemplate(templateData); // ✅ Call API to create template
      setSuccessAlert(true); // ✅ Show success alert
      setTimeout(() => {
        setSuccessAlert(false);
        props.onHide(); // ✅ Close modal after success
      }, 2000);
      // ✅ Clear form after successful submission
      setTemplateTitle("");
      setTemplateDescription("");
      setTemplateStatus("Active");
      setTemplatePracticeId("");
      setTemplateInitial("");
      setNotes([]);
    } catch (error) {
      setErrorAlert(error.message); // ✅ Show error alert if API fails
    }
  };

  function handleClose() {
    setCloseAlert(true);
  }

  const handleCancelNo = () => {
    setCloseAlert(false);
  };

  const handleCancelYes = () => {
    setTemplateData({
      templateTitle: "",
      templateDescription: "",
      templateStatus: "Active",
      templatePracticeId: "",
      templateInitial: "initial",
      notes: [],
    });
    setCloseAlert(false);
    setErrors({});
    props.onHide();
  };

  return (
    <Modal
      {...props}
      size="lg"
      centered
      aria-labelledby="contained-modal-title-vcenter"
      onHide={handleClose}
    >
      <Modal.Header
        closeButton={!closeAlert && !successAlert}
        className="border-secondary"
      >
        <div className="w-100">
          <Modal.Title
            id="contained-modal-title-vcenter"
            className="text-primary"
          >
            Add Template
          </Modal.Title>
          
        </div>
      </Modal.Header>
{closeAlert && (
            <Alert
              variant="info"
              className="d-flex align-items-center justify-content-between fs-6 fw-normal custom-alert"
            >
              <span>
                Are you sure you want to close? Changes will not be saved.
              </span>
              <div className="ms-auto d-flex">
                <Button
                  variant="outline-primary"
                  className="me-1"
                  onClick={handleCancelYes}
                >
                  <FaCheck />
                </Button>
                <Button
                  variant="outline-secondary"
                  className="me-1"
                  onClick={handleCancelNo}
                >
                  <FaTimes />
                </Button>
              </div>
            </Alert>
          )}

          {successAlert && (
            <Alert
              variant="success"
              className="d-flex align-items-center justify-content-between fs-6 fw-normal custom-alert"
            >
              <span>The template was added successfully!</span>
            </Alert>
          )}
      <Modal.Body>
        <Form onSubmit={handleSubmit}>
          {/* ✅ Template General Info */}
          <h6 className="text-primary fw-bold">Template Information</h6>
          <div className="ps-3">
            <Form.Group className="mb-3">
              <Form.Label>Template Title</Form.Label>
              <Form.Control
                type="text"
                name="templateTitle"
                value={templateTitle}
                onChange={(e) => {
                  setTemplateTitle(e.target.value);
                  setTemplateData((prev) => ({
                    ...prev,
                    templateTitle: e.target.value,
                  }));
                }}
                isInvalid={!!errors.templateTitle}
              />
              <Form.Control.Feedback type="invalid">
                {errors.templateTitle}
              </Form.Control.Feedback>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Template Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={templateDescription}
                onChange={(e) => {
                  setTemplateDescription(e.target.value);
                  setTemplateData((prev) => ({
                    ...prev,
                    templateDescription: e.target.value,
                  }));
                }}
                isInvalid={!!errors.templateDescription}
              />
              <Form.Control.Feedback type="invalid">
                {errors.templateDescription}
              </Form.Control.Feedback>
            </Form.Group>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Template Status</Form.Label>
                  <Form.Select
                    value={templateStatus}
                    isInvalid={!!errors.templateStatus}
                    onChange={(e) => {
                      setTemplateStatus(e.target.value);
                      setTemplateData((prev) => ({
                        ...prev,
                        templateStatus: e.target.value,
                      }));
                    }}
                  >
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    {errors.templateStatus}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Template Type</Form.Label>
                  <Form.Select
                    value={templateInitial}
                    onChange={(e) => {
                      setTemplateInitial(e.target.value);
                      setTemplateData((prev) => ({
                        ...prev,
                        templateInitial: e.target.value,
                      }));
                    }}
                    isInvalid={!!errors.templateInitial}
                  >
                    <option value="initial">Initial Assessment Template</option>
                    <option value="followup">Follow Up Template</option>
                    <option value="reportexport">Report Export Template</option>
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    {errors.templateInitial}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Form.Group as={Col} controlId="practiceCheckboxes">
                <Form.Label>Select Practices</Form.Label>
                <div>
                  {pracName.map((prac) => (
                    <Form.Check
                      key={prac.practiceNumber}
                      type="checkbox"
                      label={prac.practiceName}
                      checked={selectedPractices.includes(prac.practiceNumber)}
                      onChange={() => handlePracticeChange(prac.practiceNumber)}
                      isInvalid={!!errors.templatePracticeId}
                    />
                  ))}
                </div>
                <Form.Control.Feedback type="invalid">
                  {errors.templatePracticeId}
                </Form.Control.Feedback>
              </Form.Group>
            </Row>
          </div>
          <hr className="custom-hr" />

          {/* ✅ Dynamically Added Sections */}
          <h6 className="text-primary fw-bold">
            Template Sections | Questions
          </h6>
          {notes.map((note, index) => (
            <div key={index} className="ps-3 mt-0">
              <Row>
                <Col md={5}>
                  <Form.Group>
                    <Form.Label>Section Name</Form.Label>
                    <Form.Control
                      type="text"
                      value={note.sessionSection}
                      onChange={(e) =>
                        handleSectionChange(
                          index,
                          "sessionSection",
                          e.target.value
                        )
                      }
                      isInvalid={!!errors[`notes-${index}-sessionSection`]} // ✅ Validation
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors[`notes-${index}-sessionSection`]}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={5} className="d-flex align-items-end">
                  <Form.Group className="w-100">
                    {" "}
                    {/* Ensures dropdown fills width */}
                    <Form.Label>Field Type</Form.Label>
                    <Form.Select
                      value={note.sessionFieldType}
                      onChange={(e) =>
                        handleSectionChange(
                          index,
                          "sessionFieldType",
                          e.target.value
                        )
                      }
                    >
                      <option value="text">Text Field</option>
                      <option value="textarea">Text Area</option>
                      <option value="multiselect">Multi-Select</option>
                      <option value="image">Image Upload</option>
                    </Form.Select>
                  </Form.Group>
                  <Button
                    variant="link"
                    size="lg"
                    className="ms-2 align-self-end" // Moves closer & aligns with dropdown
                    onClick={() => handleRemoveSection(index)}
                  >
                    <i className="fa-regular fa-trash-can text-danger fa-xl"></i>{" "}
                    {/* fa-xl for 50% larger size */}
                  </Button>
                </Col>
              </Row>

              {/* ✅ Conditional Field Based on Type */}
              {note.sessionFieldType === "text" && (
                <Form.Group className="mt-2">
                  <Form.Label>Default Value</Form.Label>
                  <Form.Control
                    type="text"
                    value={note.sessionSectionNotes}
                    onChange={(e) =>
                      handleSectionChange(
                        index,
                        "sessionSectionNotes",
                        e.target.value
                      )
                    }
                  />
                </Form.Group>
              )}

              {note.sessionFieldType === "textarea" && (
                <Form.Group className="mt-2">
                  <Form.Label>Default Text</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    value={note.sessionSectionNotes}
                    onChange={(e) =>
                      handleSectionChange(
                        index,
                        "sessionSectionNotes",
                        e.target.value
                      )
                    }
                  />
                </Form.Group>
              )}

              {note.sessionFieldType === "multiselect" && (
                <Form.Group className="mt-2">
                  <Form.Label>Options (Comma Separated)</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Option1, Option2, Option3"
                    onChange={(e) =>
                      handleMultiSelectOptions(index, e.target.value)
                    }
                    isInvalid={!!errors[`notes-${index}-sessionSectionNotes`]}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors[`notes-${index}-sessionSectionNotes`]}
                  </Form.Control.Feedback>
                </Form.Group>
              )}

              {note.sessionFieldType === "image" && (
                <Form.Group className="mt-2">
                  <Form.Label>Upload Image</Form.Label>
                  <Form.Control
                    type="file"
                    accept="image/png, image/jpeg, image/jpg"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        const updatedNotes = [...notes];

                        // ✅ Validate File Type
                        const allowedTypes = [
                          "image/png",
                          "image/jpeg",
                          "image/jpg",
                        ];
                        if (!allowedTypes.includes(file.type)) {
                          setErrors((prevErrors) => ({
                            ...prevErrors,
                            [`notes-${index}-sessionSectionNotes`]:
                              "Only PNG, JPEG, and JPG files are allowed.",
                          }));
                          return; // ❌ Stop execution if invalid type
                        }

                        // ✅ Validate File Size (Max 5MB)
                        const maxSize = 5 * 1024 * 1024; // 5MB
                        if (file.size > maxSize) {
                          setErrors((prevErrors) => ({
                            ...prevErrors,
                            [`notes-${index}-sessionSectionNotes`]:
                              "File size must be less than 5MB.",
                          }));
                          return; // ❌ Stop execution if file too large
                        }

                        // ✅ Reset Errors if Valid
                        setErrors((prevErrors) => {
                          const newErrors = { ...prevErrors };
                          delete newErrors[
                            `notes-${index}-sessionSectionNotes`
                          ];
                          return newErrors;
                        });

                        // ✅ Store the selected file
                        updatedNotes[index].sessionSectionNotes = file;
                        setNotes(updatedNotes);
                      }
                    }}
                    isInvalid={!!errors[`notes-${index}-sessionSectionNotes`]}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors[`notes-${index}-sessionSectionNotes`]}
                  </Form.Control.Feedback>
                </Form.Group>
              )}

              <hr className="custom-hr-light" />
            </div>
          ))}

          {/* ✅ Add Section Button */}
          <div className="d-flex justify-content-start mt-0">
            <Button
              variant="outline-secondary"
              className="border-radius-0"
              onClick={handleAddSection}
            >
              + Add Section
            </Button>
          </div>

          {/* ✅ Submit Button */}
          <div className="d-flex justify-content-center align-items-center mt-3">
            <Button variant="primary" type="submit" className="px-5 py-2">
              Submit
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
}

export function AddTemplate({ onTemplateUpdate }) {
  const [templateModalShow, setTemplateModalShow] = useState(false);

  return (
    <div>
      <Button
        variant="primary"
        className="me-2 w-100"
        onClick={() => setTemplateModalShow(true)}
      >
        Add Template
      </Button>
      <AddTemplateModal
        show={templateModalShow}
        onHide={() => {
          setTemplateModalShow(false);
          onTemplateUpdate(); // ✅ Refresh template list when modal closes
        }}
      />
    </div>
  );
}
